<template>
  <HgModal
    v-model="internalModel"
    data-cy="confirmationModal"
    :containerCssClass="$style.modalContainer"
    :closeButtonCssClass="$style.closeButton"
    @close="close"
  >
    <h2 v-if="title" :class="$style.modalHeader">{{ title }}</h2>
    <div :class="[$style.modalContent, { [$style.withoutTitle]: !title }]"><slot /></div>
    <div :class="$style.buttonContainer">
      <HgButton
        ref="cancelAction"
        :class="buttonStyle"
        theme="secondary-bordered"
        type="button"
        @click="cancel"
      >
        {{ t('general.buttons.cancel') }}
      </HgButton>
      <HgButton
        ref="confirmAction"
        :disabled="isAsyncCallInProgress"
        :class="buttonStyle"
        theme="primary"
        type="button"
        @click="confirm"
      >
        <LoadingIndicator v-if="isAsyncCallInProgress" :class="$style.loadingIndicator" />
        {{ confirmText }}
      </HgButton>
    </div>
  </HgModal>
</template>

<script setup lang="ts">
import { HgButton, HgModal } from '@smg-real-estate/domus';
import { computed, type PropType } from 'vue';

import LoadingIndicator from '@/assets/images/loading_indicator.svg?component';
import { EventAction, EventLabel } from '@/services/tracking/GtmTrackingService';
import { trackClickedElement } from '@/services/tracking/Tracking';
import { useInsertionsStore } from '@/stores/insertions/useInsertionsStore';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const emit = defineEmits(['confirm', 'update:modelValue', 'closeDialog']);

type TrackingContext = 'archive' | 'delete';
type TrackingTrigger = 'cancel' | 'close' | 'confirm';

const defaultTrackingActions: Record<TrackingContext, Record<TrackingTrigger, EventAction>> = {
  archive: {
    cancel: EventAction.CANCEL_ARCHIVING,
    close: EventAction.CLOSE_ARCHIVING,
    confirm: EventAction.CONFIRM_ARCHIVING,
  },
  delete: {
    cancel: EventAction.CANCEL_DELETION,
    close: EventAction.CLOSE_DELETION,
    confirm: EventAction.CONFIRM_DELETION,
  },
};

const {
  modelValue,
  confirmText,
  buttonStyle,
  title,
  getterForAsyncCallInProgress,
  insertionId,
  trackingCancelAction,
  trackingCloseAction,
  trackingConfirmAction,
  trackingLabel,
  trackingListingId,
  trackingContext,
} = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  confirmText: {
    type: String,
    required: true,
  },
  buttonStyle: {
    type: String,
    required: false,
    default: undefined,
  },
  title: {
    type: String,
    required: false,
    default: undefined,
  },
  getterForAsyncCallInProgress: {
    type: Function,
    required: false,
    default: undefined,
  },
  insertionId: {
    type: String,
    required: false,
    default: undefined,
  },
  trackingCancelAction: {
    type: String as PropType<EventAction>,
    required: false,
    default: undefined,
  },
  trackingCloseAction: {
    type: String as PropType<EventAction>,
    required: false,
    default: undefined,
  },
  trackingConfirmAction: {
    type: String as PropType<EventAction>,
    required: false,
    default: undefined,
  },
  trackingLabel: {
    type: String,
    required: false,
    default: EventLabel.IN_MODAL,
  },
  trackingListingId: {
    type: String,
    required: false,
    default: undefined,
  },
  trackingContext: {
    type: String as PropType<TrackingContext>,
    required: false,
    default: undefined,
  },
});

const internalModel = computed({
  get() {
    return modelValue;
  },
  set(value: boolean) {
    emit('update:modelValue', value);
    if (!value) {
      emit('closeDialog');
    }
  },
});

const isAsyncCallInProgress = computed<boolean>(() => {
  if (!getterForAsyncCallInProgress) {
    return false;
  }
  return getterForAsyncCallInProgress();
});

//   methods: {
const closeModal = (): void => {
  internalModel.value = false;
};

const getTrackingListingId = (): string | undefined => {
  if (trackingListingId) {
    return trackingListingId;
  }
  if (!insertionId) {
    return undefined;
  }

  return (
    useInsertionsStore().insertions.find((item) => item.id === insertionId)?.listing.id ?? undefined
  );
};

const trackAction = (action: EventAction): void => {
  trackClickedElement({
    action,
    label: trackingLabel,
    listingId: getTrackingListingId(),
  });
};

const tracking = (trigger: TrackingTrigger, actionOverride?: EventAction): void => {
  if (actionOverride) {
    trackAction(actionOverride);
    return;
  }

  if (trackingContext) {
    trackAction(defaultTrackingActions[trackingContext][trigger]);
  }
};

const close = (): void => {
  tracking('close', trackingCloseAction);
  closeModal();
};

const cancel = (): void => {
  tracking('cancel', trackingCancelAction);
  closeModal();
};

const confirm = (): void => {
  tracking('confirm', trackingConfirmAction);
  emit('confirm');
};
</script>

<style module lang="scss">
@import '@/assets/variables';

.modalContainer {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 0;
  overflow-y: auto;
}

@media (min-width: $viewport-sm-min) {
  .modalContainer {
    width: auto;
    height: auto;
  }
}

.modalHeader {
  @include heading-3;

  & {
    padding: $space-75 $space-300 $space-75 $space-150;
    margin: 0;
    border-bottom: 1px solid $color-grey-50;
  }
}

.modalContent {
  max-width: $modal-content-max-width;
  padding: $space-150;

  &.withoutTitle {
    margin-top: $space-300;
  }
}

.closeButton {
  margin-top: $space-50;
  margin-right: $space-25;
}

.buttonContainer {
  display: flex;
  justify-content: flex-end;
  margin-bottom: $space-75;
}

.loadingIndicator {
  width: var(--typo-body-3-font-size);
  padding-right: $space-100;
  filter: grayscale(1) brightness(300%);
}
</style>
